import React, { useEffect, useState, useRef, Children, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Sparkles, Star } from 'lucide-react';
import { SnowfallEffect } from './components/SnowfallEffect';
import { GoogleTagManager } from './components/GoogleTagManager';
import { Footer } from './components/Footer';
// Animation variants for staggered entrance
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function App() {
  const [showStickyButton, setShowStickyButton] = useState(false);
  const buttonRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (buttonRef.current) {
        const buttonTop = buttonRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        // Show sticky button when original button scrolls past viewport
        setShowStickyButton(buttonTop < 0);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen w-full bg-[#2d1b4e] text-white overflow-x-hidden relative pb-20" data-id="element-0">
      <GoogleTagManager data-id="element-1" />
      <SnowfallEffect data-id="element-2" />

      {/* Background Stars (Static) */}
      <div className="fixed inset-0 z-0 opacity-30 pointer-events-none" data-id="element-3">
        {[...Array(20)].map((_, i) => <div key={i} className="absolute rounded-full bg-yellow-200" style={{
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        width: Math.random() * 3 + 1 + 'px',
        height: Math.random() * 3 + 1 + 'px',
        animation: `twinkle ${Math.random() * 3 + 2}s infinite`
      }} data-id="element-4" />)}
      </div>

      <main className="relative z-10 max-w-5xl mx-auto px-4 py-4 space-y-12 pb-12" data-id="element-5">
        {/* Hero Section */}
        <motion.section initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8
      }} className="w-full bg-gradient-to-b from-[#dc2626] to-[#b91c1c] rounded-3xl p-6 md:p-10 text-center shadow-[0_0_40px_rgba(220,38,38,0.4)] border-2 border-red-400/30 relative overflow-hidden" data-id="element-6">
          {/* Decorative sparkles on card */}
          <Sparkles className="absolute top-8 left-8 text-yellow-300 w-8 h-8 animate-pulse" data-id="element-7" />
          <Sparkles className="absolute bottom-8 right-8 text-yellow-300 w-6 h-6 animate-pulse delay-700" data-id="element-8" />

          {/* Santa Image */}
          <div className="relative w-full max-w-md mx-auto mb-6" data-id="element-9">
            <div className="relative w-[100%] max-w-[100%] mx-auto" data-id="element-10">
              <img src="https://cdn.magicpatterns.com/uploads/ibtfhVbw2t5nqKDPyQoFcK/15f4b3cb26c39fe064d874b5ddeae3c4249b7de1.png" alt="Santa Claus with bell and personalized letter" className="w-full h-full object-contain drop-shadow-2xl" data-id="element-11" />
            </div>
          </div>

          <h1 className="text-3xl md:text-6xl font-bold text-[#fbbf24] mb-4 drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)] tracking-wide" data-id="element-12">
            Letters from Santa
          </h1>

          <h2 className="text-xl md:text-xl font-semibold text-white mb-4 drop-shadow-md" data-id="element-13">
            Magical Personalised Letters Direct from the North Pole!
          </h2>

          <p className="text-[#fef3c7] italic text-lg flex items-center justify-center gap-2" data-id="element-14">
            Creating Christmas memories that last a lifetime{' '}
            <span className="not-italic" data-id="element-15">✨</span>
          </p>
        </motion.section>

        {/* Call to Action Section */}
        <motion.section initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="bg-white rounded-3xl p-4 md:p-6 text-center shadow-2xl border-2 border-slate-200" data-id="element-16">
          <h2 className="text-xl md:text-4xl font-bold text-[#1e3a8a] mb-4" data-id="element-17">
            You're just one click away from the moment they'll remember forever
          </h2>
          <motion.a ref={buttonRef} href="https://trk.topreviewspicks.com/click" animate={{
          scale: [1, 1.03, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.98
        }} className="inline-block bg-[#22c55e] hover:bg-[#16a34a] text-white font-bold text-xl md:text-2xl py-4 px-10 rounded-full shadow-lg transition-colors duration-200 mb-4 no-underline" data-id="element-18">
            Show Me the Magic Letter! 🎅
          </motion.a>
          <p className="text-lg md:text-3xl font-bold text-[#dc2626] mb-4" data-id="element-19">
            Limited Free Bonus Gifts Available
          </p>
        </motion.section>

        {/* What You Get Section */}
        <motion.section initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="bg-white/95 rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-[#fbbf24]/30" data-id="element-20">
          <h2 className="text-2xl md:text-5xl font-bold text-center mb-12" data-id="element-21">
            <span className="text-[#dc2626] italic" style={{
            fontFamily: 'Pacifico, cursive'
          }} data-id="element-22">
              What do you get with a
            </span>
            <br data-id="element-23" />
            <span className="text-[#16a34a] italic" style={{
            fontFamily: 'Pacifico, cursive'
          }} data-id="element-24">
              Letter From Santa?
            </span>
          </h2>

          <div className="max-w-2xl mx-auto" data-id="element-25">
            <div className="space-y-6" data-id="element-26">
              {[{
              icon: '📜',
              title: 'LETTER',
              titleColor: '#dc2626',
              description: "A personalized letter from Santa Claus thanking them for being so good this year! Santa will mention your child, their friend's name, and the name of their town so your little one knows that Santa is really thinking of them."
            }, {
              icon: '🎖️',
              title: 'GOOD BEHAVIOR CERTIFICATE',
              titleColor: '#dc2626',
              description: 'An official certificate for Good Behavior! This certificate is signed by Santa and features a beautiful gold seal. Perfect for placing on the mantel among your holiday cards and festive decor.'
            }, {
              icon: '📋',
              title: 'NICE LIST',
              titleColor: '#dc2626',
              description: "A real nice list with your child's name! Your child will delight in seeing their name in writing and make them feel proud of being good this year."
            }, {
              icon: '✉️',
              title: 'ENVELOPE',
              titleColor: '#dc2626',
              description: 'A specially designed holiday envelope holds everything together. There will be no doubt that this letter came directly from the North Pole!'
            }].map((item, idx) => <div key={idx} className="flex gap-4 items-start" data-id="element-27">
                  <div className="flex-shrink-0 w-16 h-16 rounded-full bg-white border-4 border-[#dc2626] flex items-center justify-center shadow-lg" data-id="element-28">
                    <span className="text-3xl" data-id="element-29">{item.icon}</span>
                  </div>
                  <div className="flex-1" data-id="element-30">
                    <h3 className="text-xl font-bold mb-2 tracking-wide" style={{
                  color: item.titleColor
                }} data-id="element-31">
                      {item.title}
                    </h3>
                    <p className="text-slate-700 leading-relaxed text-sm" data-id="element-32">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.section>

        {/* Info Cards Grid */}

        {/* Feature Cards Grid */}
        <motion.section variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-33"></motion.section>

        {/* How It Works */}

        {/* Pricing Card */}
      </main>

      <Footer data-id="element-34" />

      {/* Sticky Bottom Button */}
      <AnimatePresence data-id="element-35">
        {showStickyButton && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="fixed bottom-0 left-0 right-0 z-50 bg-[#2d1b4e] border-t-4 border-[#22c55e] shadow-2xl py-4 px-4" data-id="element-36">
            <div className="max-w-5xl mx-auto" data-id="element-37">
              <motion.a href="https://trk.topreviewspicks.com/click" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="block w-full bg-[#22c55e] hover:bg-[#16a34a] text-white font-bold text-lg md:text-xl py-4 px-6 rounded-full shadow-lg transition-colors duration-200 text-center no-underline" data-id="element-38">
                Show Me the Magic Letter! 🎅
              </motion.a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}